import { createSlice } from '@reduxjs/toolkit'
import { API } from 'src/utils/interceptor'
import {
  RejectedNotification,
  SuccessNotification,
} from 'src/notifications/SuccessNotification'
import Sentry from 'src/utils/sentry'
import {
  mergeMenuListItemWithSalesMixPatch,
  mergeSavedMenuIntoCurrentMenu,
} from 'src/utils/menuSalesMixDisplay'

const initialState = {
  menus: [],
  currentMenu: null,
  menuSections: [],
  menuRecipes: [],
  availableRecipes: [],
  products: [],
  isLoading: false,
  error: {},
}

const menuBuilderSlice = createSlice({
  name: 'menus',
  initialState,
  reducers: {
    setIsLoading: (state, action) => {
      state.isLoading = action.payload
    },
    setMenus: (state, action) => {
      state.menus = action.payload
    },
    setCurrentMenu: (state, action) => {
      state.currentMenu = action.payload
    },
    setMenuSections: (state, action) => {
      state.menuSections = action.payload
    },
    setMenuRecipes: (state, action) => {
      state.menuRecipes = action.payload
    },
    setAvailableRecipes: (state, action) => {
      state.availableRecipes = action.payload
    },
    setProducts: (state, action) => {
      state.products = action.payload.reduce((accumulator, currentItem) => {
        // Check if the current item's _id is already in the accumulator
        if (!accumulator.some(item => item._id === currentItem._id)) {
          // If not, add it to the accumulator
          accumulator.push(currentItem)
        }
        return accumulator
      }, [])
    },
    setError: (state, action) => {
      // Only store serializable error information
      state.error = {
        message: action.payload?.message || 'An error occurred',
        status: action.payload?.status || 500,
        timestamp: new Date().toISOString(),
      }
    },
  },
})

// Menu actions
export const fetchAllMenus = venueId => async dispatch => {
  dispatch(setIsLoading(true))
  try {
    const data = await API.get('api2', `menus/${venueId}`)
    dispatch(setMenus(data?.menus || []))
    dispatch(setIsLoading(false))
  } catch (error) {
    console.error('Error fetching menus:', error)
    console.error('Error details:', error.response?.data || error.message)
    dispatch(
      setError({
        message:
          error.response?.data?.message ||
          error.message ||
          'Failed to fetch menus',
        status: error.response?.status || 500,
      }),
    )
    dispatch(setIsLoading(false))
    Sentry.captureException(error)
  }
}

export const createMenu = (venueId, menuData) => async dispatch => {
  dispatch(setIsLoading(true))
  try {
    const response = await API.post('api2', `menus/${venueId}`, {
      body: menuData,
    })
    SuccessNotification({ message: response?.message })
    dispatch(setIsLoading(false))
    await dispatch(fetchAllMenus(venueId))
  } catch (error) {
    console.error('Error creating menu:', error)
    dispatch(setIsLoading(false))
    const message =
      error?.response?.data?.message ||
      error?.message ||
      'Failed to create menu'
    dispatch(
      setError({
        message,
        status: error.response?.status || 500,
      }),
    )
    RejectedNotification({ message })
    Sentry.captureException(error)
  }
}

export const updateMenu = (menuId, menuData, venueId) => async dispatch => {
  dispatch(setIsLoading(true))
  try {
    const response = await API.put('api2', `menus/${venueId}/${menuId}`, {
      body: menuData,
    })
    SuccessNotification({ message: response?.message })
    dispatch(setIsLoading(false))
    await dispatch(fetchAllMenus(venueId))
  } catch (error) {
    dispatch(setIsLoading(false))
    const message =
      error?.response?.data?.message ||
      error?.message ||
      'Failed to update menu'
    dispatch(
      setError({
        message,
        status: error.response?.status || 500,
      }),
    )
    RejectedNotification({ message })
    Sentry.captureException(error)
  }
}

export const duplicateMenu = (menuId, newName, venueId) => async dispatch => {
  dispatch(setIsLoading(true))
  try {
    const response = await API.post(
      'api2',
      `menus/${venueId}/${menuId}/duplicate`,
      {
        body: { name: newName },
      },
    )
    SuccessNotification({ message: response?.message })
    dispatch(setIsLoading(false))
    await dispatch(fetchAllMenus(venueId))
  } catch (error) {
    dispatch(setIsLoading(false))
    const message =
      error?.response?.data?.message ||
      error?.message ||
      'Failed to duplicate menu'
    dispatch(
      setError({
        message,
        status: error.response?.status || 500,
      }),
    )
    RejectedNotification({ message })
    Sentry.captureException(error)
  }
}

export const archiveMenu = (menuId, venueId) => async dispatch => {
  dispatch(setIsLoading(true))
  try {
    const response = await API.put('api2', `menus/${venueId}/${menuId}/archive`)
    SuccessNotification({ message: response?.message })
    dispatch(setIsLoading(false))
    await dispatch(fetchAllMenus(venueId))
  } catch (error) {
    dispatch(setIsLoading(false))
    const message =
      error?.response?.data?.message ||
      error?.message ||
      'Failed to archive menu'
    dispatch(
      setError({
        message,
        status: error.response?.status || 500,
      }),
    )
    RejectedNotification({ message })
    Sentry.captureException(error)
  }
}

export const restoreMenu = (menuId, venueId) => async dispatch => {
  dispatch(setIsLoading(true))
  try {
    const response = await API.put('api2', `menus/${venueId}/${menuId}/restore`)
    SuccessNotification({ message: response?.message })
    dispatch(setIsLoading(false))
    await dispatch(fetchAllMenus(venueId))
  } catch (error) {
    dispatch(setIsLoading(false))
    const message =
      error?.response?.data?.message ||
      error?.message ||
      'Failed to restore menu'
    dispatch(
      setError({
        message,
        status: error.response?.status || 500,
      }),
    )
    RejectedNotification({ message })
    Sentry.captureException(error)
  }
}

export const deleteMenu = (menuId, venueId) => async dispatch => {
  dispatch(setIsLoading(true))
  try {
    const response = await API.del('api2', `menus/${venueId}/${menuId}`)
    SuccessNotification({ message: response?.message })
    dispatch(setIsLoading(false))
    await dispatch(fetchAllMenus(venueId))
  } catch (error) {
    dispatch(setIsLoading(false))
    const message =
      error?.response?.data?.message ||
      error?.message ||
      'Failed to delete menu'
    dispatch(
      setError({
        message,
        status: error.response?.status || 500,
      }),
    )
    RejectedNotification({ message })
    Sentry.captureException(error)
  }
}

export const updateMenuSalesMix =
  (menuId, entries) => async (dispatch, getState) => {
    try {
      const response = await API.patch('api2', `menus/${menuId}/sales-mix`, {
        body: { entries },
      })

      const savedMenu = response?.menu
      if (savedMenu) {
        const { currentMenu, menus } = getState().menus

        if (currentMenu && String(currentMenu._id) === String(menuId)) {
          dispatch(
            setCurrentMenu(
              mergeSavedMenuIntoCurrentMenu(currentMenu, savedMenu),
            ),
          )
        }

        dispatch(
          setMenus(
            (menus || []).map(item =>
              String(item._id) === String(menuId)
                ? mergeMenuListItemWithSalesMixPatch(item, savedMenu)
                : item,
            ),
          ),
        )
      }

      SuccessNotification({
        message: response?.message || 'Sales mix updated successfully',
      })

      return response
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update sales mix'

      dispatch(
        setError({
          message,
          status: error.response?.status || 500,
        }),
      )
      RejectedNotification({ message })
      Sentry.captureException(error)
      throw new Error(message)
    }
  }

export const fetchMenuDetails = menuId => async (dispatch, getState) => {
  dispatch(setIsLoading(true))
  try {
    const response = await API.get('api2', `menus/${menuId}/details`)
    const menu = response.menu

    dispatch(setCurrentMenu(menu))

    const { menus } = getState().menus
    if (menu?._id && menus?.length) {
      dispatch(
        setMenus(
          menus.map(item =>
            String(item._id) === String(menu._id)
              ? mergeMenuListItemWithSalesMixPatch(item, menu)
              : item,
          ),
        ),
      )
    }

    // Set sections from the menu
    dispatch(setMenuSections(menu.sections || []))

    // Convert recipesBySection to flat array of menuRecipes for backwards compatibility
    const allMenuRecipes = []

    // New data structure: recipesBySection with recipe objects directly
    if (menu.recipesBySection) {
      Object.entries(menu.recipesBySection).forEach(
        ([sectionId, sectionData]) => {
          if (sectionData.recipes) {
            // Map recipes to the old structure with section reference
            sectionData.recipes.forEach(recipe => {
              allMenuRecipes.push({
                recipe: recipe,
                section: sectionId,
              })
            })
          }
        },
      )
    }

    // Also support old structure where sections have recipes array
    if (menu.sections) {
      menu.sections.forEach(section => {
        if (section.recipes) {
          section.recipes.forEach(recipe => {
            // Avoid duplicates
            if (!allMenuRecipes.find(mr => mr.recipe._id === recipe._id)) {
              allMenuRecipes.push({
                recipe: recipe,
                section: section._id,
              })
            }
          })
        }
      })
    }

    // Add unassigned recipes if they exist
    if (menu.unassignedRecipes) {
      menu.unassignedRecipes.forEach(recipe => {
        allMenuRecipes.push({
          recipe: recipe,
          section: null,
        })
      })
    }

    dispatch(setMenuRecipes(allMenuRecipes))

    dispatch(setIsLoading(false))
    return response // Return the response so it can be used in download
  } catch (error) {
    dispatch(setIsLoading(false))
    dispatch(
      setError({
        message:
          error.response?.data?.message ||
          error.message ||
          'Failed to fetch menu details',
        status: error.response?.status || 500,
      }),
    )
    Sentry.captureException(error)
    throw error // Re-throw the error so it can be caught in download
  }
}

// Menu Section actions
export const createMenuSection = (menuId, sectionData) => async dispatch => {
  dispatch(setIsLoading(true))
  try {
    const response = await API.post('api2', `menus/${menuId}/sections`, {
      body: sectionData,
    })
    SuccessNotification({ message: response?.message })
    dispatch(setIsLoading(false))
    await dispatch(fetchMenuDetails(menuId))
  } catch (error) {
    dispatch(setIsLoading(false))
    const message =
      error?.response?.data?.message ||
      error?.message ||
      'Failed to create section'
    dispatch(
      setError({
        message,
        status: error.response?.status || 500,
      }),
    )
    RejectedNotification({ message })
    Sentry.captureException(error)
  }
}

export const updateMenuSection =
  (sectionId, sectionData, menuId) => async dispatch => {
    dispatch(setIsLoading(true))
    try {
      const response = await API.patch(
        'api2',
        `menus/${menuId}/sections/${sectionId}`,
        {
          body: sectionData,
        },
      )
      SuccessNotification({ message: response?.message })
      dispatch(setIsLoading(false))
      await dispatch(fetchMenuDetails(menuId))
    } catch (error) {
      dispatch(setIsLoading(false))
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update section'
      dispatch(
        setError({
          message:
            error.response?.data?.message ||
            error.message ||
            'An error occurred',
          status: error.response?.status || 500,
        }),
      )
      RejectedNotification({ message })
      Sentry.captureException(error)
    }
  }

export const deleteMenuSection = (sectionId, menuId) => async dispatch => {
  dispatch(setIsLoading(true))
  try {
    const response = await API.del(
      'api2',
      `menus/${menuId}/sections/${sectionId}`,
    )
    SuccessNotification({ message: response?.message })
    dispatch(setIsLoading(false))
    await dispatch(fetchMenuDetails(menuId))
  } catch (error) {
    dispatch(setIsLoading(false))
    const message =
      error?.response?.data?.message ||
      error?.message ||
      'Failed to delete section'
    dispatch(
      setError({
        message:
          error.response?.data?.message || error.message || 'An error occurred',
        status: error.response?.status || 500,
      }),
    )
    RejectedNotification({ message })
    Sentry.captureException(error)
  }
}

export const reorderMenuSections =
  (menuId, sectionOrders) => async dispatch => {
    dispatch(setIsLoading(true))
    try {
      const response = await API.patch(
        'api2',
        `menus/${menuId}/sections/reorder`,
        {
          body: { sectionOrders },
        },
      )
      SuccessNotification({ message: response?.message })
      dispatch(setIsLoading(false))

      try {
        await dispatch(fetchMenuDetails(menuId))
      } catch (refreshError) {
        console.error('Redux: Error refreshing menu details:', refreshError)
      }
    } catch (error) {
      console.error('Redux: Error reordering sections:', error)
      dispatch(setIsLoading(false))
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to reorder sections'
      dispatch(
        setError({
          message:
            error.response?.data?.message ||
            error.message ||
            'An error occurred',
          status: error.response?.status || 500,
        }),
      )
      RejectedNotification({ message })
      Sentry.captureException(error)
    }
  }

// Menu Recipe actions
export const fetchAvailableRecipes =
  (menuId, search = '') =>
  async dispatch => {
    try {
      const response = await API.get(
        'api2',
        `menus/${menuId}/available-recipes`,
        {
          queryStringParameters: { search },
        },
      )
      dispatch(setAvailableRecipes(response.recipes))
    } catch (error) {
      console.error('Error fetching available recipes:', error)
      dispatch(
        setError({
          message:
            error.response?.data?.message ||
            error.message ||
            'An error occurred',
          status: error.response?.status || 500,
        }),
      )
      Sentry.captureException(error)
    }
  }

export const addRecipesToMenu =
  (menuId, recipeIds, sectionId) => async dispatch => {
    dispatch(setIsLoading(true))
    try {
      const response = await API.post('api2', `menus/${menuId}/recipes`, {
        body: { recipeIds, sectionId },
      })
      SuccessNotification({ message: response?.message })
      dispatch(setIsLoading(false))
      await dispatch(fetchMenuDetails(menuId))
    } catch (error) {
      dispatch(setIsLoading(false))
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to add recipes to menu'
      dispatch(
        setError({
          message:
            error.response?.data?.message ||
            error.message ||
            'An error occurred',
          status: error.response?.status || 500,
        }),
      )
      RejectedNotification({ message })
      Sentry.captureException(error)
    }
  }

export const updateRecipePlacement =
  (menuId, recipeId, placementData) => async dispatch => {
    dispatch(setIsLoading(true))
    try {
      const response = await API.patch(
        'api2',
        `menus/${menuId}/recipes/${recipeId}`,
        {
          body: placementData,
        },
      )
      SuccessNotification({ message: response?.message })
      dispatch(setIsLoading(false))
      await dispatch(fetchMenuDetails(menuId))
    } catch (error) {
      dispatch(setIsLoading(false))
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update recipe placement'
      dispatch(
        setError({
          message:
            error.response?.data?.message ||
            error.message ||
            'An error occurred',
          status: error.response?.status || 500,
        }),
      )
      RejectedNotification({ message })
      Sentry.captureException(error)
    }
  }

export const removeRecipeFromMenu =
  (menuId, sectionId, recipeId) => async dispatch => {
    dispatch(setIsLoading(true))
    try {
      const response = await API.del(
        'api2',
        `menus/${sectionId}/recipes/${recipeId}`,
      )
      SuccessNotification({ message: response?.message })
      dispatch(setIsLoading(false))
      await dispatch(fetchMenuDetails(menuId))
    } catch (error) {
      dispatch(setIsLoading(false))
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to remove recipe from menu'
      dispatch(
        setError({
          message:
            error.response?.data?.message ||
            error.message ||
            'An error occurred',
          status: error.response?.status || 500,
        }),
      )
      RejectedNotification({ message })
      Sentry.captureException(error)
    }
  }

export const reorderMenuRecipes = (menuId, recipeOrders) => async dispatch => {
  dispatch(setIsLoading(true))
  try {
    const response = await API.patch(
      'api2',
      `menus/${menuId}/recipes/reorder`,
      {
        body: { recipeOrders },
      },
    )
    SuccessNotification({ message: response?.message })
    dispatch(setIsLoading(false))
    await dispatch(fetchMenuDetails(menuId))
  } catch (error) {
    dispatch(setIsLoading(false))
    const message =
      error?.response?.data?.message ||
      error?.message ||
      'Failed to reorder recipes'
    dispatch(
      setError({
        message:
          error.response?.data?.message || error.message || 'An error occurred',
        status: error.response?.status || 500,
      }),
    )
    RejectedNotification({ message })
    Sentry.captureException(error)
  }
}

export const fetchMenuAllergenGrid = menuId => async dispatch => {
  try {
    const response = await API.get('api2', `menus/${menuId}/allergen-grid`)
    return { payload: response }
  } catch (error) {
    dispatch(
      setError({
        message:
          error.response?.data?.message || error.message || 'An error occurred',
        status: error.response?.status || 500,
      }),
    )
    Sentry.captureException(error)
    throw error
  }
}

export const getProducts = venueId => async dispatch => {
  try {
    const products = await API.get('api', `venue/${venueId}/products`)
    dispatch(setProducts(products))
  } catch (error) {
    dispatch(
      setError({
        message:
          error.response?.data?.message || error.message || 'An error occurred',
        status: error.response?.status || 500,
      }),
    )
    Sentry.captureException(error)
  }
}
// export const createMenuItem = (venueId, formData) => async dispatch => {
//   setIsLoading(true)
//   try {
//     const session = await Auth.currentSession();
//     const token = session.getAccessToken().getJwtToken();

//     await fetch(`${config.apiGateway.EXPRESS_SERVER_URL}menus/createMenuItem`, {
//       method: 'POST',
//       body: formData,
//       headers: {
//         'Content-Type': 'multipart/form-data',
//         'Authorization': `${token}`,
//       },
//     })
//     await dispatch(fetchAllMenus(venueId))
//     setIsLoading(false)
//   } catch (error) {
//     dispatch(setError({
//       message: error.response?.data?.message || error.message || 'An error occurred',
//       status: error.response?.status || 500
//     }))
//   }
// }

export const {
  setIsLoading,
  setMenus,
  setCurrentMenu,
  setMenuSections,
  setMenuRecipes,
  setAvailableRecipes,
  setError,
  setProducts,
} = menuBuilderSlice.actions

export default menuBuilderSlice
